import React, { createContext, useContext, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, MessageCircle, Check } from "lucide-react";
import { useI18n } from "../i18n/LanguageContext";
import { createRequest, buildWhatsAppLink } from "../lib/api";
import { toast } from "sonner";

const RequestModalContext = createContext(null);
export const useRequestModal = () => useContext(RequestModalContext);

const CATEGORY_KEYS = ["aviation", "yacht", "chauffeur", "villa", "dining", "nightlife", "security", "experience", "event", "lifestyle", "other"];
const URGENCY_KEYS = ["whenever", "today", "within_3_hours", "urgent"];
const CONTACT_KEYS = ["whatsapp", "phone", "email"];

export const RequestModalProvider = ({ children }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [done, setDone] = useState(false);
  const [waLink, setWaLink] = useState("#");
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    message: "", name: "", email: "", phone: "", contact_method: "whatsapp",
    category: "other", urgency: "whenever", location: "", date: "",
  });

  const openRequest = useCallback((category, prefillMessage) => {
    setDone(false);
    setForm((f) => ({ ...f, category: category || "other", message: prefillMessage || "" }));
    setOpen(true);
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast.error(t("request.required"));
      return;
    }
    setSubmitting(true);
    try {
      await createRequest({ ...form, source: "make_a_request" });
      const link = buildWhatsAppLink({
        ...form,
        category: t(`request.categories.${form.category}`),
        urgency: t(`request.urgencies.${form.urgency}`),
      });
      setWaLink(link);
      setDone(true);
      toast.success(t("request.success"));
    } catch (err) {
      console.error("VELMONTÉ request submit failed:", err?.response?.status, err?.message);
      toast.error("Error. Please try WhatsApp.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls = "vm-input rounded-none";

  return (
    <RequestModalContext.Provider value={{ openRequest }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-start md:items-center justify-center overflow-y-auto"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="request-modal-overlay"
          >
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={() => setOpen(false)} />
            <motion.div
              className="relative w-full max-w-2xl bg-vm-surface border border-vm-border my-8 mx-4 p-8 md:p-12"
              initial={{ y: 24, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 24, opacity: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-5 right-5 text-vm-muted hover:text-vm-text transition-colors"
                aria-label="Close" data-testid="request-modal-close"
              >
                <X strokeWidth={1.25} size={22} />
              </button>

              {done ? (
                <div className="py-8 text-center" data-testid="request-success">
                  <div className="mx-auto mb-6 w-14 h-14 border border-vm-accent flex items-center justify-center">
                    <Check strokeWidth={1} size={26} className="text-vm-accent" />
                  </div>
                  <h3 className="font-serif text-3xl font-light mb-3">{t("request.success")}</h3>
                  <p className="text-vm-text2 text-sm mb-8">{t("common.availability")}</p>
                  <a
                    href={waLink} target="_blank" rel="noopener noreferrer"
                    className="vm-btn-primary inline-flex items-center gap-2 px-8 py-3 text-sm tracking-wide"
                    data-testid="request-continue-whatsapp"
                  >
                    <MessageCircle strokeWidth={1.5} size={18} /> {t("request.openWhatsApp")}
                  </a>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="request-form">
                  <p className="vm-label mb-3 text-vm-accent">{t("common.makeRequest")}</p>
                  <h3 className="font-serif text-3xl md:text-4xl font-light mb-5 leading-tight">{t("request.title")}</h3>
                  <div className="vm-hairline w-16 mb-8" style={{ background: "var(--vm-accent)" }} />

                  <div className="mb-5">
                    <textarea
                      value={form.message} onChange={(e) => set("message", e.target.value)}
                      placeholder={t("request.placeholder")} rows={4}
                      className={inputCls + " resize-none"} data-testid="request-message-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-5 mb-5">
                    <div>
                      <label className="vm-field-label">{t("common.name")}</label>
                      <input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder={t("common.name")} className={inputCls} data-testid="request-name-input" />
                    </div>
                    <div>
                      <label className="vm-field-label">{t("common.phone")}</label>
                      <input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder={t("common.phone")} className={inputCls} data-testid="request-phone-input" />
                    </div>
                    <div>
                      <label className="vm-field-label">{t("common.emailLabel")}</label>
                      <input value={form.email} onChange={(e) => set("email", e.target.value)} placeholder={t("common.emailLabel")} className={inputCls} data-testid="request-email-input" />
                    </div>
                    <div>
                      <label className="vm-field-label">{t("common.from") + " / " + t("common.to")}</label>
                      <input value={form.location} onChange={(e) => set("location", e.target.value)} placeholder={t("common.from") + " / " + t("common.to")} className={inputCls} data-testid="request-location-input" />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-8">
                    <div>
                      <label className="vm-field-label">{t("request.category")}</label>
                      <select value={form.category} onChange={(e) => set("category", e.target.value)} className={inputCls} data-testid="request-category-select">
                        {CATEGORY_KEYS.map((c) => <option key={c} value={c}>{t(`request.categories.${c}`)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="vm-field-label">{t("request.urgency")}</label>
                      <select value={form.urgency} onChange={(e) => set("urgency", e.target.value)} className={inputCls} data-testid="request-urgency-select">
                        {URGENCY_KEYS.map((u) => <option key={u} value={u}>{t(`request.urgencies.${u}`)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="vm-field-label">{t("request.contactMethod")}</label>
                      <select value={form.contact_method} onChange={(e) => set("contact_method", e.target.value)} className={inputCls} data-testid="request-contact-select">
                        {CONTACT_KEYS.map((c) => <option key={c} value={c}>{t(`request.contactMethods.${c}`)}</option>)}
                      </select>
                    </div>
                  </div>

                  <button type="submit" disabled={submitting} className="vm-btn-primary w-full inline-flex items-center justify-center gap-2 py-4 text-sm tracking-widest uppercase disabled:opacity-50" data-testid="request-submit-button">
                    <Send strokeWidth={1.5} size={16} /> {submitting ? t("request.sending") : t("common.send")}
                  </button>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </RequestModalContext.Provider>
  );
};
